import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock } from "lucide-react";

const MAX_BIO = 290;

export function ProfileEditor() {
  const { user, refresh } = useAuth();
  const [bio, setBio] = useState("");
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setBio(user.bio || "");
      setRoles(user.roles || []);
    }
  }, [user]);

  const toggleRole = (r) => {
    setRoles((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/users/me/profile", { bio: bio.slice(0, MAX_BIO), roles });
      toast.success("Profilo aggiornato");
      if (refresh) await refresh();
    } catch {
      toast.error("Errore nell'aggiornamento del profilo");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const remaining = MAX_BIO - bio.length;
  const profileUrl = user.username_slug ? `/utente/${user.username_slug}` : null;

  return (
    <section className="card-surface p-8" data-testid="profile-editor">
      <p className="label-eyebrow text-[#C05C45]">Personalizza</p>
      <h2 className="mt-2 font-display text-2xl text-[#1A362D]">Il tuo profilo nella community</h2>

      <div className="mt-3 flex items-start gap-2 rounded-lg bg-[#FAF3E7] border border-[#E8DCC4] p-3">
        <Lock className="w-4 h-4 text-[#C05C45] mt-0.5 flex-shrink-0" />
        <p className="font-ui text-xs text-[#5C6B64] leading-relaxed">
          I dati presenti in questa scheda sono visibili <strong>solo all&apos;interno di questa community</strong> (utenti registrati). Non vengono indicizzati da Google né mostrati a visitatori esterni.
        </p>
      </div>

      <form onSubmit={handleSave} className="mt-6 space-y-6">
        {/* Bio */}
        <div>
          <label htmlFor="bio" className="font-ui text-sm font-medium text-[#1A362D]">
            Una frase su di te <span className="text-[#8A9691] font-normal">(facoltativo)</span>
          </label>
          <textarea
            id="bio"
            data-testid="profile-bio-input"
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, MAX_BIO))}
            maxLength={MAX_BIO}
            rows={3}
            placeholder="Es: Mamma di due bimbi curiosi. Cerco libri Montessori e idee per l'autonomia."
            className="mt-2 w-full px-3 py-2 rounded-lg border border-[#E8E6E1] bg-white font-ui text-sm text-[#1A362D] focus:outline-none focus:border-[#C05C45] transition-colors resize-none"
          />
          <p className={`mt-1 font-ui text-xs ${remaining < 30 ? "text-[#C05C45]" : "text-[#8A9691]"}`} data-testid="profile-bio-counter">
            {remaining} caratteri rimasti
          </p>
        </div>

        {/* Roles */}
        <div>
          <p className="font-ui text-sm font-medium text-[#1A362D]">Come ti descrivi?</p>
          <p className="mt-1 font-ui text-xs text-[#8A9691]">Puoi scegliere entrambe le voci.</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <label className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-all ${roles.includes("genitore") ? "bg-[#1A362D] text-white border-[#1A362D]" : "bg-white text-[#1A362D] border-[#E8E6E1] hover:border-[#C05C45]"}`}>
              <input
                type="checkbox"
                data-testid="profile-role-genitore"
                checked={roles.includes("genitore")}
                onChange={() => toggleRole("genitore")}
                className="sr-only"
              />
              <span className="font-ui text-sm">Genitore</span>
            </label>
            <label className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-all ${roles.includes("professionista") ? "bg-[#1A362D] text-white border-[#1A362D]" : "bg-white text-[#1A362D] border-[#E8E6E1] hover:border-[#C05C45]"}`}>
              <input
                type="checkbox"
                data-testid="profile-role-professionista"
                checked={roles.includes("professionista")}
                onChange={() => toggleRole("professionista")}
                className="sr-only"
              />
              <span className="font-ui text-sm">Professionista</span>
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-2 divider">
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-save-btn"
            className="btn-primary text-sm disabled:opacity-50"
          >
            {saving ? "Salvataggio…" : "Salva profilo"}
          </button>
          {profileUrl && (
            <Link
              to={profileUrl}
              data-testid="profile-public-link"
              className="font-ui text-xs text-[#C05C45] hover:underline"
            >
              Vedi il tuo profilo pubblico →
            </Link>
          )}
        </div>
      </form>
    </section>
  );
}
